type MailingAddressParts = {
  mailingAddressLine1?: string | null;
  mailingAddressLine2?: string | null;
  mailingCity?: string | null;
  mailingState?: string | null;
  mailingPostalCode?: string | null;
  mailingCountryCode?: string | null;
};

function isUsLikeCountry(countryCode?: string | null): boolean {
  const normalized = (countryCode || '').trim().toUpperCase();
  return normalized === 'US' || normalized === 'USA';
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function normalizeStreetLineForDisplay(
  line1?: string | null,
  countryCode?: string | null
): string {
  const raw = normalizeWhitespace(line1 || '');
  if (!raw) return '';

  // Only apply this heuristic to US addresses.
  if (!isUsLikeCountry(countryCode)) return raw;

  // Leave canonical formats untouched.
  if (/^\d/.test(raw)) return raw;
  if (/^P\.?\s*O\.?\s*BOX\b/i.test(raw)) return raw;

  // Handle "Street Name 1254 Apt 3" -> "1254 Street Name Apt 3"
  const trailingUnitMatch = raw.match(
    /^(.+?)\s+(\d+[A-Za-z0-9-]*)\s+((?:APT|APARTMENT|UNIT|STE|SUITE|#)\s*.+)$/i
  );
  if (trailingUnitMatch) {
    const street = normalizeWhitespace(trailingUnitMatch[1]);
    const number = trailingUnitMatch[2];
    const unit = normalizeWhitespace(trailingUnitMatch[3]);
    return `${number} ${street} ${unit}`;
  }

  // Handle "Street Name 1254" -> "1254 Street Name"
  const trailingNumberMatch = raw.match(/^(.+?)\s+(\d+[A-Za-z0-9-]*)$/);
  if (trailingNumberMatch) {
    const street = normalizeWhitespace(trailingNumberMatch[1]);
    const number = trailingNumberMatch[2];
    return `${number} ${street}`;
  }

  return raw;
}

export function formatMailingAddressForDisplay(parts: MailingAddressParts): string {
  const normalizedLine1 = normalizeStreetLineForDisplay(
    parts.mailingAddressLine1,
    parts.mailingCountryCode
  );

  return [
    normalizedLine1,
    parts.mailingAddressLine2,
    parts.mailingCity,
    parts.mailingState,
    parts.mailingPostalCode,
    parts.mailingCountryCode
  ]
    .map((segment) => (segment ? normalizeWhitespace(segment) : ''))
    .filter(Boolean)
    .join(', ');
}
